import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { untrack } from 'svelte';
import Button from '$lib/components/ui/button/button.svelte';
import Input from '$lib/components/ui/input/input.svelte';
import Label from '$lib/components/ui/label/label.svelte';
import { Checkbox } from '$lib/components/ui/checkbox';
import { X, Moon, AlertTriangle } from '$lib/icons';

var root_3 = $.from_html(`<!> Copy nights shares`, 1);

var root_4 = $.from_html(`<div class="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" role="alert"><div class="flex items-start gap-2"><!> <p class="text-xs text-amber-800">This will overwrite all current values with each participant's <strong>nights × share</strong>. These values are a static snapshot — future
                changes to nights or share will not be reflected here automatically.</p></div> <div class="flex gap-2"><!> <!></div></div>`);

var root_7 = $.from_html(`<div class="flex items-center gap-2 min-h-[44px]" role="listitem"><!> <!> <!></div>`);
var root_1 = $.from_html(`<div class="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="share-edit-modal-title" tabindex="-1"><div role="presentation" class="bg-background rounded-lg shadow-lg w-full max-w-[380px] animate-in fade-in zoom-in-95"><div class="flex items-center justify-between p-4 border-b"><h2 id="share-edit-modal-title" class="text-lg font-semibold">Edit Shares</h2> <!></div> <div class="p-4 space-y-3"><div class="flex items-center justify-between"><p class="text-xs text-muted-foreground">Select participants and set their share parts</p> <span> </span></div> <!> <!> <div class="max-h-[300px] overflow-y-auto space-y-2" role="list" aria-label="Participant shares"></div></div> <div class="flex gap-2 p-4 border-t"><!> <!></div></div></div>`);

export default function ShareEditModal($$anchor, $$props) {
	$.push($$props, true);

	/**
	 * ShareEditModal - Sub-modal for editing participant shares in FREE split mode.
	 * Opens from the ExpenseEditModal when user clicks "Edit shares".
	 * Works on local copies and only commits changes on confirm.
	 */
	// Local working copies
	let localParts = $.state($.proxy({}));

	let localChecked = $.state($.proxy({}));
	let localPreviousValues = $.state($.proxy({}));

	// Initialize local state when modal opens
	$.user_effect(() => {
		if ($$props.open) {
			untrack(() => {
				$.set(localParts, { ...$$props.initialData.shareParts }, true);
				$.set(localChecked, { ...$$props.initialData.shareChecked }, true);
				$.set(localPreviousValues, { ...$$props.initialData.sharePreviousValues }, true);
			});
		}
	});

	const localTotalParts = $.derived(() => Object.values($.get(localParts)).reduce(
		(sum, val) => {
			const numVal = typeof val === 'number' ? val : parseFloat(String(val)) || 0;

			return sum + numVal;
		},
		0
	));

	const isLocalPartsValid = $.derived(() => $.get(localTotalParts) > 0);

	function handleCheckChange(participantId, checked) {
		$.get(localChecked)[participantId] = checked;

		if (checked) {
			$.get(localParts)[participantId] = $.get(localPreviousValues)[participantId] || 1;
		} else {
			const currentVal = typeof $.get(localParts)[participantId] === 'number'
				? $.get(localParts)[participantId]
				: parseFloat(String($.get(localParts)[participantId])) || 0;

			if (currentVal > 0) {
				$.get(localPreviousValues)[participantId] = currentVal;
			}

			$.get(localParts)[participantId] = 0;
		}
	}

	function handleConfirm() {
		$$props.onConfirm({
			shareParts: { ...$.get(localParts) },
			shareChecked: { ...$.get(localChecked) },
			sharePreviousValues: { ...$.get(localPreviousValues) }
		});
	}

	let showNightsWarning = $.state(false);

	function copyNightsShares() {
		$.set(showNightsWarning, true);
	}

	function applyNightsShares() {
		for (const participant of $$props.participants) {
			const value = participant.nights * participant.share;

			$.get(localParts)[participant.id] = value;
			$.get(localChecked)[participant.id] = true;
			$.get(localPreviousValues)[participant.id] = value;
		}

		$.set(showNightsWarning, false);
	}

	function cancelNightsWarning() {
		$.set(showNightsWarning, false);
	}

	function handleBackdropClick(event) {
		if (event.target === event.currentTarget) {
			$$props.onCancel();
		}
	}

	function handleKeydown(event) {
		if (!$$props.open) return;

		if (event.key === 'Escape') {
			event.stopPropagation();
			$$props.onCancel();
		}
	}

	var fragment = $.comment();

	$.event('keydown', $.window, handleKeydown);

	var node = $.first_child(fragment);

	{
		var consequent_1 = ($$anchor) => {
			var div = root_1();

			div.__click = handleBackdropClick;

			var div_1 = $.child(div);

			div_1.__click = (e) => e.stopPropagation();

			var div_2 = $.child(div_1);
			var node_1 = $.sibling($.child(div_2), 2);

			Button(node_1, {
				variant: 'ghost',
				size: 'sm',

				get onclick() {
					return $$props.onCancel;
				},

				class: 'min-h-[44px] min-w-[44px]',
				'aria-label': 'Close',

				children: ($$anchor, $$slotProps) => {
					X($$anchor, { class: 'h-4 w-4' });
				},

				$$slots: { default: true }
			});

			$.reset(div_2);

			var div_3 = $.sibling(div_2, 2);
			var div_4 = $.child(div_3);
			var span = $.sibling($.child(div_4), 2);
			var text = $.child(span);

			$.reset(span);
			$.reset(div_4);

			var node_2 = $.sibling(div_4, 2);

			Button(node_2, {
				variant: 'outline',
				size: 'sm',
				onclick: copyNightsShares,
				class: 'w-full min-h-[44px] gap-2',

				children: ($$anchor, $$slotProps) => {
					var fragment_2 = root_3();
					var node_3 = $.first_child(fragment_2);

					Moon(node_3, { class: 'h-4 w-4' });
					$.next();
					$.append($$anchor, fragment_2);
				},

				$$slots: { default: true }
			});

			var node_4 = $.sibling(node_2, 2);

			{
				var consequent = ($$anchor) => {
					var div_5 = root_4();
					var div_6 = $.child(div_5);
					var node_5 = $.child(div_6);

					AlertTriangle(node_5, { class: 'h-4 w-4 text-amber-600 mt-0.5 shrink-0' });
					$.next(2);
					$.reset(div_6);

					var div_7 = $.sibling(div_6, 2);
					var node_6 = $.child(div_7);

					Button(node_6, {
						size: 'sm',
						onclick: applyNightsShares,
						class: 'flex-1 min-h-[36px]',

						children: ($$anchor, $$slotProps) => {
							$.next();

							var text_1 = $.text('Apply');

							$.append($$anchor, text_1);
						},

						$$slots: { default: true }
					});

					var node_7 = $.sibling(node_6, 2);

					Button(node_7, {
						variant: 'outline',
						size: 'sm',
						onclick: cancelNightsWarning,
						class: 'flex-1 min-h-[36px]',

						children: ($$anchor, $$slotProps) => {
							$.next();

							var text_2 = $.text('Cancel');

							$.append($$anchor, text_2);
						},

						$$slots: { default: true }
					});

					$.reset(div_7);
					$.reset(div_5);
					$.append($$anchor, div_5);
				};

				$.if(node_4, ($$render) => {
					if ($.get(showNightsWarning)) $$render(consequent);
				});
			}

			var div_8 = $.sibling(node_4, 2);

			$.each(div_8, 21, () => $$props.participants, (participant) => participant.id, ($$anchor, participant) => {
				var div_9 = root_7();
				var node_8 = $.child(div_9);

				Checkbox(node_8, {
					get id() {
						return `share-check-${$.get(participant).id ?? ''}`;
					},

					get checked() {
						return $.get(localChecked)[$.get(participant).id];
					},

					onchange: (e) => handleCheckChange($.get(participant).id, e.target.checked),

					get 'aria-label'() {
						return `Include ${$.get(participant).name ?? ''}`;
					},

					class: 'min-w-[20px] min-h-[20px]'
				});

				var node_9 = $.sibling(node_8, 2);

				Label(node_9, {
					get for() {
						return `share-check-${$.get(participant).id ?? ''}`;
					},

					class: 'flex-1 text-sm cursor-pointer',

					children: ($$anchor, $$slotProps) => {
						$.next();

						var text_3 = $.text();

						$.template_effect(() => $.set_text(text_3, $.get(participant).name));
						$.append($$anchor, text_3);
					},

					$$slots: { default: true }
				});

				var node_10 = $.sibling(node_9, 2);

				{
					let $0 = $.derived(() => !$.get(localChecked)[$.get(participant).id]);

					Input(node_10, {
						get id() {
							return `share-parts-${$.get(participant).id ?? ''}`;
						},

						type: 'number',
						step: '0.01',
						min: '0',
						placeholder: '0',

						get disabled() {
							return $.get($0);
						},

						get 'aria-label'() {
							return `${$.get(participant).name ?? ''} parts`;
						},

						class: 'w-24 min-h-[44px]',

						get value() {
							return $.get(localParts)[$.get(participant).id];
						},

						set value($$value) {
							$.get(localParts)[$.get(participant).id] = $$value;
						}
					});
				}

				$.reset(div_9);
				$.append($$anchor, div_9);
			});

			$.reset(div_8);
			$.reset(div_3);

			var div_10 = $.sibling(div_3, 2);
			var node_11 = $.child(div_10);

			{
				let $0 = $.derived(() => !$.get(isLocalPartsValid));

				Button(node_11, {
					onclick: handleConfirm,

					get disabled() {
						return $.get($0);
					},

					class: 'flex-1 min-h-[44px]',

					children: ($$anchor, $$slotProps) => {
						$.next();

						var text_4 = $.text('Confirm');

						$.append($$anchor, text_4);
					},

					$$slots: { default: true }
				});
			}

			var node_12 = $.sibling(node_11, 2);

			Button(node_12, {
				variant: 'outline',

				get onclick() {
					return $$props.onCancel;
				},

				class: 'flex-1 min-h-[44px]',

				children: ($$anchor, $$slotProps) => {
					$.next();

					var text_5 = $.text('Cancel');

					$.append($$anchor, text_5);
				},

				$$slots: { default: true }
			});

			$.reset(div_10);
			$.reset(div_1);
			$.reset(div);

			$.template_effect(
				($0) => {
					$.set_class(span, 1, `text-sm ${$.get(isLocalPartsValid) ? 'text-green-600' : 'text-destructive'}`);
					$.set_text(text, `${$0 ?? ''} parts ${$.get(isLocalPartsValid) ? '✓' : '⚠'}`);
				},
				[() => $.get(localTotalParts).toFixed(2)]
			);

			$.append($$anchor, div);
		};

		$.if(node, ($$render) => {
			if ($$props.open) $$render(consequent_1);
		});
	}

	$.append($$anchor, fragment);
	$.pop();
}

$.delegate(['click']);
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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